import Promise from 'bluebird'
import { HeaderComponent } from 'components/header/component'
import { preventDefaultThen } from 'components/link'
import { Loading } from 'components/loading'
import { RasaContext } from 'context'
import { AjaxWrapper, HttpMethod } from 'generic/ajaxWrapper'
import * as GenericRedux from 'generic/genericRedux'
import { chunk } from 'lodash/fp'
import React, { Component } from 'react'
import { Button, ButtonGroup, Input } from 'reactstrap'
import * as Redux from 'redux'
import { FileSelector } from 'shared/components'
import { injectReducer } from 'store/index'

import './_styles.scss'
import * as Constants from './constants'
import * as Types from './types'
import { fallbackName, parseSourceCsv, sampleCsvDataUri, summarizeStatuses } from './utils'

class BulkSourceComponent extends Component<Types.Props, Types.State> {
  public static contextType = RasaContext

  constructor(props: Types.Props) {
    super(props)
    this.state = Constants.InitialState
  }

  public componentDidMount = () => {
    this.context.user.init().then(({activeCommunity}) => {
      this.setState({
        isLoading: false,
        selectedCommunity: activeCommunity.communityId,
      })
    })
  }

  public render() {
    const busy = this.state.isChecking || this.state.isSaving
    return <div className="source-check-wrapper">
      <div className="stay-banner">Don&apos;t leave the page</div>
      <HeaderComponent
        title={'Bulk Source Import'}
        subTitle={'Bulk Source Import'}
        description={[
          'Check a list of RSS feed URLs and add the valid ones to the active community as sources.',
          'Paste URLs or upload a CSV with a url column and an optional name column.',
        ]}/>
      {this.state.isLoading ?
        <Loading size="64"/>
        :
        <div>
          <ButtonGroup className="mode-toggle">
            <Button color="primary" outline={this.state.mode !== 'paste'} disabled={busy}
                    onClick={() => this.setMode('paste')}>Paste URLs</Button>
            <Button color="primary" outline={this.state.mode !== 'csv'} disabled={busy}
                    onClick={() => this.setMode('csv')}>Upload CSV</Button>
          </ButtonGroup>
          <div className="input-area">
            <form onSubmit={preventDefaultThen(() => this.checkUrls())}>
              {this.state.mode === 'paste' ?
                <Input autoFocus disabled={busy} type="textarea" value={this.state.url.join('\n')}
                       placeholder="Enter the URLs to review, one per line"
                       onChange={(e: any) => this.handleTextAreaChange(e)} />
                :
                <div>
                  <FileSelector key={this.state.csvKey}
                                sampleFileLocation={sampleCsvDataUri()}
                                onSelect={this.onCsvSelected} />
                  <div className="csv-message">{this.state.csvMessage}</div>
                </div>
              }
              <div className="form-button-wrap">
                <Button disabled={busy || this.state.url.length === 0}>Fetch</Button>
                {this.state.url.length > 0 && <Button disabled={busy} onClick={this.reset}>Reset</Button>}
              </div>
            </form>
          </div>
          <div className="info-msg">
            {this.renderProgress()}
            {this.state.data.length > 0 &&
                <div className="table-responsive table-wrapper">
                    <table className="table result-table">
                        <thead>
                        <tr>
                            <th>Url</th>
                            <th>Name</th>
                            <th>Status</th>
                        </tr>
                        </thead>
                        <tbody>
                        {this.state.data.map((result) => (
                          <tr key={result.url} className={this.rowClass(result.status)}>
                            <td>{result.url}</td>
                            <td>{result.name}</td>
                            <td>{result.status}</td>
                          </tr>))}
                        </tbody>
                    </table>
                  {this.state.isSaving ? <Loading size="32"/> :
                    this.saveableRows().length > 0 && !this.state.isChecking &&
                      <Button onClick={this.addSources}>Save {this.saveableRows().length} Sources</Button>}
                </div>
            }
            <div className="warning">{this.state.error}</div>
            <div className="success">{this.state.success ? `Saved ${this.state.saved} of ${this.state.saveTotal} sources.` : ''}</div>
          </div>
        </div>
      }
    </div>
  }

  private renderProgress() {
    if (this.state.isChecking) {
      return <div className="progress-msg">Checking {this.state.checked} of {this.state.url.length} URLs...</div>
    }
    if (this.state.isSaving) {
      return <div className="progress-msg">
        Saving {this.state.saved + this.state.saveFailed} of {this.state.saveTotal} sources...
      </div>
    }
    if (this.state.summary) {
      return <div className="progress-msg">{this.state.summary}</div>
    }
    return null
  }

  private rowClass(status: string): string {
    switch (status) {
      case Constants.STATUS_INVALID:
      case Constants.STATUS_CHECK_FAILED:
      case Constants.STATUS_SAVE_FAILED:
        return 'row-failed'
      case Constants.STATUS_SAVED:
        return 'row-saved'
      default:
        return ''
    }
  }

  private setMode = (mode: Types.ImportMode) => {
    this.setState({
      ...Constants.InitialState,
      csvKey: this.state.csvKey + 1,
      isLoading: false,
      mode,
      selectedCommunity: this.state.selectedCommunity,
    })
  }

  private reset = () => {
    this.setMode(this.state.mode)
  }

  private handleTextAreaChange = (e) => {
    const urlsArray = e.target.value.split(/[\n,]+/)
    this.setState({url: urlsArray})
  }

  private onCsvSelected = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e: any) => {
      const parsed = parseSourceCsv(e.target.result || '')
      if (parsed.error) {
        this.setState({ csvKey: this.state.csvKey + 1, csvMessage: '', error: parsed.error, names: {}, url: [] })
        return
      }
      const notes = []
      if (parsed.skipped) {
        notes.push(`${parsed.skipped} rows skipped with no url`)
      }
      if (parsed.duplicates) {
        notes.push(`${parsed.duplicates} duplicate urls ignored`)
      }
      this.setState({
        csvMessage: `Read ${parsed.urls.length} URLs from ${file.name}.` + (notes.length ? ` ${notes.join(', ')}.` : ''),
        data: [],
        error: '',
        names: parsed.names,
        success: false,
        summary: '',
        url: parsed.urls,
      })
    }
    reader.onerror = () => this.setState({ error: 'Unable to read the CSV file.' })
    reader.readAsText(file)
  }

  private cleanUrls = (): string[] => {
    const trimmed = this.state.url.map((u) => u.trim()).filter((u) => u.length > 0)
    return trimmed.filter((u, index) => trimmed.indexOf(u) === index)
  }

  private saveableRows = (): Types.SourceCheckResult[] =>
    this.state.data.filter((row) => row.status === Constants.STATUS_FOUND && row.url && row.name)

  private checkUrls = () => {
    const urls = this.cleanUrls()
    const pending = urls.map((url) => ({ url, name: this.state.names[url] || '', status: Constants.STATUS_PENDING }))
    this.setState({
      checked: 0,
      data: pending,
      error: '',
      isChecking: true,
      saveFailed: 0,
      saved: 0,
      success: false,
      summary: '',
      url: urls,
    }, () =>
      Promise.map(chunk(Constants.CHUNK_SIZE, urls), (batch: string[]) => this.checkBatch(batch),
                  { concurrency: Constants.CONCURRENCY })
        .then(() => this.setState((state) => ({ isChecking: false, summary: summarizeStatuses(state.data) })))
        .catch(() => this.setState({ error: 'An error occurred while checking the URLs.', isChecking: false })))
  }

  private checkBatch = (batch: string[]) => {
    return AjaxWrapper
      .ajax(`${AjaxWrapper.getServerUrl()}/cs/${this.state.selectedCommunity}/bulk-source`, HttpMethod.POST, {
        url: batch,
        headers: this.state.headers,
      })
      .catch(() => null)
      .then((response: any) => {
        const results: Types.SourceCheckResult[] = Array.isArray(response) ?
          response : batch.map((url) => ({ url, name: '', status: Constants.STATUS_CHECK_FAILED }))
        const byUrl: Record<string, Types.SourceCheckResult> = {}
        results.forEach((result) => byUrl[result.url] = result)
        this.setState((state) => ({
          checked: state.checked + batch.length,
          data: state.data.map((row) => byUrl[row.url] ? this.mergeResult(row, byUrl[row.url]) : row),
        }))
      })
  }

  // The CSV name wins, then the name discovered from the feed, then the hostname.
  private mergeResult = (row: Types.SourceCheckResult, result: Types.SourceCheckResult): Types.SourceCheckResult => ({
    url: row.url,
    name: this.state.names[row.url] || result.name || fallbackName(row.url),
    status: result.status,
  })

  private addSources = () => {
    const rows = this.saveableRows()
    this.setState({ error: '', isSaving: true, saveFailed: 0, saveTotal: rows.length, saved: 0, success: false }, () =>
      Promise.map(chunk(Constants.CHUNK_SIZE, rows), (batch: Types.SourceCheckResult[]) => this.saveBatch(batch),
                  { concurrency: Constants.CONCURRENCY })
        .then(() => this.setState((state) => ({
          error: state.saveFailed > 0 ? `${state.saveFailed} sources failed to save. See the table for details.` : '',
          isSaving: false,
          success: true,
          summary: summarizeStatuses(state.data),
        })))
        .catch(() => this.setState({ error: 'An error occurred while saving the sources.', isSaving: false })))
  }

  private saveBatch = (batch: Types.SourceCheckResult[]) => {
    return AjaxWrapper
      .ajax(`${AjaxWrapper.getServerUrl()}/cs/${this.state.selectedCommunity}/bulk-source-add`, HttpMethod.POST, {
        payload: batch.map(({ url, name }) => ({ url, name })),
      })
      .then(() => Constants.STATUS_SAVED)
      .catch(() => Constants.STATUS_SAVE_FAILED)
      .then((status: string) => {
        const urls = new Set(batch.map((row) => row.url))
        this.setState((state) => ({
          data: state.data.map((row) => urls.has(row.url) ? { ...row, status } : row),
          saveFailed: state.saveFailed + (status === Constants.STATUS_SAVE_FAILED ? batch.length : 0),
          saved: state.saved + (status === Constants.STATUS_SAVED ? batch.length : 0),
        }))
      })
  }
}

export const BulkSourceImport = GenericRedux.createConnect(BulkSourceComponent, Constants.REDUX_STORE_HOME)
injectReducer(
  Constants.REDUX_STORE_HOME,
  Redux.combineReducers({
    data: GenericRedux.createGenericReducer(Constants.REDUX_STORE_HOME, {}),
  }),
)
